import React, { useEffect } from "react";
import { Accordion } from "@tvg/fuse/accordion";
import { Link } from "@tvg/fuse/link";
import { gridConfig } from "@tvg/fuse/grid";

import useMediaQuery from "client/hooks/useMediaQuery";

import footerLinksConfig from "src/config/footer-links.json";
import type { Brand, FuseBrand } from "src/types/brand";
import type { TransformedBrandConfig } from "src/types/amplience";
import styles from "./link-section.module.css";

export interface AccordionLinksProps {
  brand: Brand;
  /** Footer links from Amplience (optional, falls back to static JSON) */
  footerLinks?: TransformedBrandConfig;
}

interface LinkItem {
  children: string;
  hrefAttr: string;
  analyticsRealEstate?: string;
  dataTestId?: string;
  id?: string;
}

interface LinkSection {
  label: string;
  links: LinkItem[];
}

type BrandConfig = Record<string, LinkSection>;

const AccordionLinks: React.FC<AccordionLinksProps> = ({
  brand,
  footerLinks,
}) => {
  const isDesktop = useMediaQuery(`(min-width: ${gridConfig.l.minWidth})`);

  useEffect(() => {
    const windowWithCookie = window as Window & {
      cookiePreferenceClick?: (e: Event) => void;
    };

    if (!windowWithCookie.cookiePreferenceClick) return;

    const manageCookiesLink = document.getElementById(
      "OneTrustCookiePreferences",
    );
    if (!manageCookiesLink) return;

    const handler = (e: Event) => {
      windowWithCookie.cookiePreferenceClick?.(e);
    };

    manageCookiesLink.addEventListener("click", handler);
    return () => {
      manageCookiesLink.removeEventListener("click", handler);
    };
  }, [isDesktop]);

  const brandKey: FuseBrand = brand.toLowerCase() as FuseBrand;

  // Use Amplience data if provided, otherwise fall back to static JSON
  const config: BrandConfig =
    (footerLinks as BrandConfig) ||
    (footerLinksConfig[brandKey] as BrandConfig) ||
    (footerLinksConfig.very as BrandConfig);

  const sections = Object.keys(config);

  const isFallBackLinks = !footerLinks || Object.keys(footerLinks).length === 1;

  return (
    <div
      className={styles.accordion_links__container}
      data-testid="accordion-links"
      data-brand={brandKey}
    >
      {isDesktop ? (
        <div
          data-testid="accordion-links-desktop"
          className={styles.accordion_links__desktop}
        >
          {sections.map((sectionKey) => {
            const section = config[sectionKey];
            return (
              <div
                key={`footer-desktop--${sectionKey}`}
                className={`${styles.accordion_links__section} ${styles[`accordion_links__section--${sectionKey}`]} ${styles[`accordion_links__section--${brandKey}`]} ${isFallBackLinks ? styles.accordion_links__sectionFallback : ""}`}
                data-testid={`footer-desktop--${sectionKey}`}
              >
                <h3 className={styles.accordion_links__heading}>
                  {section.label}
                </h3>
                <ul className={styles.accordion_links__list}>
                  {section.links.map((link) => (
                    <li key={link.id || link.hrefAttr || link.children}>
                      <Link
                        id={link.id || undefined}
                        href={link.hrefAttr}
                        size="medium"
                        data-testid={link.dataTestId}
                        data-analytics-real-estate={link.analyticsRealEstate}
                      >
                        {link.children}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          data-testid="accordion-links-mobile"
          className={styles.accordion_links__mobile}
        >
          {sections.map((sectionKey, index) => {
            const section = config[sectionKey];
            return (
              <Accordion
                key={`footer-accordion--${sectionKey}`}
                heading={section.label}
                open={index === 0}
                dataTestId={`footer-accordion--${sectionKey}`}
                className={styles.accordion_links__accordion}
              >
                <ul
                  className={`${styles.accordion_links__list} ${styles.accordion_links__mobilelist}`}
                >
                  {section.links.map((link) => (
                    <li key={link.id || link.hrefAttr || link.children}>
                      <Link
                        id={link.id || undefined}
                        href={link.hrefAttr}
                        size="medium"
                        data-testid={link.dataTestId}
                        data-analytics-real-estate={link.analyticsRealEstate}
                      >
                        {link.children}
                      </Link>
                    </li>
                  ))}
                </ul>
              </Accordion>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AccordionLinks;
